/**
 * Helpers tz-aware de fecha/hora.
 *
 * Regla: timestamps se guardan SIEMPRE en UTC (`timestamptz` en Postgres).
 * Toda conversión a "día local", formateo para display y cálculo de límites
 * de día pasa por estas funciones usando la zona de la clínica (no la del
 * navegador), para que el día "21 de abril" signifique lo mismo para la
 * doctora en CDMX que para una secretaria consultando desde otra zona.
 */
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { es } from 'date-fns/locale'

export const DEFAULT_TZ = 'America/Mexico_City'

function safeFormat(iso: string | Date, tz: string, pattern: string): string {
  try {
    return formatInTimeZone(iso, tz, pattern, { locale: es })
  } catch {
    return ''
  }
}

/** "YYYY-MM-DD" del día de HOY según la zona de la clínica. */
export function clinicToday(tz: string = DEFAULT_TZ): string {
  return formatInTimeZone(new Date(), tz, 'yyyy-MM-dd')
}

/** "YYYY-MM-DD" del día de `iso` según la zona de la clínica. */
export function clinicDate(iso: string | Date, tz: string = DEFAULT_TZ): string {
  return formatInTimeZone(iso, tz, 'yyyy-MM-dd')
}

/**
 * Días completos transcurridos desde `iso` hasta HOY, contados por día-calendario
 * en la zona de la clínica (no por horas). Negativo si `iso` es futuro, 0 si es hoy.
 * Útil para "hace X días" y umbrales de antigüedad (p.ej. medicación sin renovar).
 */
export function daysSince(iso: string | Date | null | undefined, tz: string = DEFAULT_TZ): number {
  if (!iso) return 0
  try {
    const then  = Date.parse(`${clinicDate(iso, tz)}T00:00:00Z`)
    const today = Date.parse(`${clinicToday(tz)}T00:00:00Z`)
    if (isNaN(then) || isNaN(today)) return 0
    return Math.round((today - then) / 86_400_000)
  } catch {
    return 0
  }
}

/**
 * Límites UTC (ISO) del día `dateISO` ("YYYY-MM-DD") en la zona de la clínica.
 * Úsalo para filtros Supabase: `.gte('starts_at', start).lt('starts_at', end)`.
 */
export function dayRangeUtc(dateISO: string, tz: string = DEFAULT_TZ): { start: string; end: string } {
  const start = fromZonedTime(`${dateISO}T00:00:00`, tz)
  const end   = new Date(start.getTime() + 24 * 60 * 60 * 1000)
  return { start: start.toISOString(), end: end.toISOString() }
}

/** Límites UTC del día de HOY en la zona de la clínica. */
export function todayRangeUtc(tz: string = DEFAULT_TZ): { start: string; end: string } {
  return dayRangeUtc(clinicToday(tz), tz)
}

/** ¿`a` y `b` caen el mismo día en la zona de la clínica? */
export function isSameClinicDay(a: string | Date, b: string | Date, tz: string = DEFAULT_TZ): boolean {
  return clinicDate(a, tz) === clinicDate(b, tz)
}

/** Desplaza `dateISO` (YYYY-MM-DD) por `days` días en la zona de la clínica. */
export function addDaysISO(dateISO: string, days: number, tz: string = DEFAULT_TZ): string {
  const { start } = dayRangeUtc(dateISO, tz)
  const shifted   = new Date(new Date(start).getTime() + days * 24 * 60 * 60 * 1000)
  return formatInTimeZone(shifted, tz, 'yyyy-MM-dd')
}

/**
 * Edad en años completos entre `birthDate` y `today`, ambos "YYYY-MM-DD" (fechas de
 * calendario ya resueltas — pasar `clinicToday()` como `today`, no un timestamp crudo).
 * Resta los años y corrige uno si el cumpleaños de este año todavía no ha ocurrido
 * (p.ej. nacido 2015-06-15, hoy 2026-01-01 → 10 años, no 11). Aritmética pura sobre los
 * componentes Y/M/D — nunca `new Date()`/reloj del sistema, así que no hay ambigüedad de
 * zona horaria que resolver.
 * Retorna `null` si alguna de las dos fechas no tiene formato "YYYY-MM-DD" — nunca inventa
 * una edad a partir de un dato mal formado.
 */
export function differenceInYears(today: string, birthDate: string): number | null {
  const t = _parseYmd(today)
  const b = _parseYmd(birthDate)
  if (!t || !b) return null
  const birthdayHasOccurred = t.m > b.m || (t.m === b.m && t.d >= b.d)
  return t.y - b.y - (birthdayHasOccurred ? 0 : 1)
}

function _parseYmd(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return null
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) }
}

// ── Formateo para display (tz-aware) ───────────────────────────────────────

/** Formato largo: "martes, 21 de abril de 2026" */
export function formatDate(
  iso: string | Date | null | undefined,
  tz: string = DEFAULT_TZ,
  pattern = "EEEE, d 'de' MMMM 'de' yyyy",
): string {
  if (!iso) return ''
  return safeFormat(iso, tz, pattern)
}

/** Formato corto de fecha: "21 abr 2026" */
export function formatDateShort(
  iso: string | Date | null | undefined,
  tz: string = DEFAULT_TZ,
): string {
  if (!iso) return ''
  return safeFormat(iso, tz, 'd MMM yyyy')
}

/**
 * Formatea fecha parcial: "2026" → "2026", "2026-06" → "jun 2026",
 * "2026-06-15" → "15 jun 2026". Útil para campos donde la paciente
 * solo recuerda el año o el mes (p.ej. último Papanicolaou).
 */
const _MONTHS_SHORT = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic']
export function formatPartialDate(
  val: string | null | undefined,
  tz: string = DEFAULT_TZ,
): string {
  if (!val) return ''
  if (val.length === 4) return val
  if (val.length === 7) {
    const [y, m] = val.split('-')
    return `${_MONTHS_SHORT[Number(m) - 1]} ${y}`
  }
  return formatDateShort(val, tz)
}

/** Formato numérico: "21/04/2026" */
export function formatDateNumeric(
  iso: string | Date | null | undefined,
  tz: string = DEFAULT_TZ,
): string {
  if (!iso) return ''
  return safeFormat(iso, tz, 'dd/MM/yyyy')
}

/** "14:30" */
export function formatTime(
  iso: string | Date | null | undefined,
  tz: string = DEFAULT_TZ,
  pattern = 'HH:mm',
): string {
  if (!iso) return ''
  return safeFormat(iso, tz, pattern)
}

/** "21 abr 2026, 14:30" */
export function formatDateTime(
  iso: string | Date | null | undefined,
  tz: string = DEFAULT_TZ,
  pattern = "d MMM yyyy, HH:mm",
): string {
  if (!iso) return ''
  return safeFormat(iso, tz, pattern)
}

/** Day-of-week corto: "mar" */
export function formatWeekdayShort(
  iso: string | Date | null | undefined,
  tz: string = DEFAULT_TZ,
): string {
  if (!iso) return ''
  return safeFormat(iso, tz, 'EEE')
}

/** Formato libre compatible con date-fns (ver https://date-fns.org/docs/format). */
export function formatTz(
  iso: string | Date | null | undefined,
  tz: string,
  pattern: string,
): string {
  if (!iso) return ''
  return safeFormat(iso, tz, pattern)
}
